// Crayo, Vite entry point.
//
// The existing components in /components/*.jsx were authored pre-bundler with
// React as a global and cross-file identifiers published on `window`. Rather
// than refactor five files into a clean ESM graph on migration night, we
// preserve that pattern and just wire Vite around it.
//
// Load order is meaningful: Shared.jsx must run first to publish
// Icon/Page/navigate/useToast/etc onto window; CreateFlow.jsx must run before
// Payment.jsx so priceForPages/formatPrice are available when Payment renders.
import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';

import './sentry-init.js';
import './utm-capture.js';
import './consent.js';
import './meta-pixel-init.js';
import './ga4-init.js';
import './clarity-init.js';
import { applyRouteMeta } from './seo.js';
import { initRouteTracking, trackRouteChange } from './route-tracking.js';

// Import the API client early so `window.CRAYO_API_BASE` is populated
// before any component renders. Components import the named exports
// directly (see CreateFlow.jsx / Payment.jsx); the window global is a
// convenience for console debugging, not a runtime dependency.
import './api.js';

// Expose React + ReactDOM on window for any legacy inline scripts that may
// still look for them. Harmless if unused.
window.React = React;
window.ReactDOM = ReactDOM;

// Side-effect imports. Order matters.
import '../components/Shared.jsx';
import '../components/Landing.jsx';
import '../components/CreateFlow.jsx';
import '../components/Payment.jsx';
import '../components/StaticPages.jsx';

import '../styles.css';

// Pull resolved globals off window now that all side-effect modules ran.
const {
  ToastProvider,
  useRoute,
  LandingPage,
  CreatePage,
  PreviewPage,
  CheckoutPage,
  DownloadPage,
  TermsPage,
  PrivacyPage,
  CookiesPage,
  AboutPage,
  ContactPage,
  NotFoundPage,
  CookieBanner,
} = window;

function App() {
  const rawPath = useRoute();
  const path = rawPath.split('?')[0];

  let page;
  if (path === '/' || path === '') {
    page = <LandingPage />;
  } else if (path === '/create') {
    page = <CreatePage />;
  } else if (path.startsWith('/preview/')) {
    const sid = path.slice('/preview/'.length);
    page = <PreviewPage sessionId={sid} />;
  } else if (path.startsWith('/checkout/')) {
    const sid = path.slice('/checkout/'.length);
    page = <CheckoutPage sessionId={sid} />;
  } else if (path.startsWith('/download/')) {
    const oid = path.slice('/download/'.length);
    page = <DownloadPage orderId={oid} />;
  } else if (path === '/termeni') {
    page = <TermsPage />;
  } else if (path === '/confidentialitate') {
    page = <PrivacyPage />;
  } else if (path === '/cookies') {
    page = <CookiesPage />;
  } else if (path === '/despre') {
    page = <AboutPage />;
  } else if (path === '/contact') {
    page = <ContactPage />;
  } else {
    page = <NotFoundPage />;
  }

  return (
    <ToastProvider>
      {page}
      <CookieBanner />
      {import.meta.env.DEV ? <SentryTestButton /> : null}
    </ToastProvider>
  );
}

// Dev-only button that throws an error so we can confirm Sentry wiring end-to-end.
// Guarded by import.meta.env.DEV so the production bundle never ships it.
function SentryTestButton() {
  const style = {
    position: 'fixed',
    bottom: 12,
    right: 12,
    zIndex: 9999,
    fontSize: 11,
    padding: '6px 10px',
    background: '#1A1A1A',
    color: '#FDF9F3',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
    opacity: 0.6,
  };
  const onClick = () => {
    // Intentionally throw so Sentry captures it via the global error handler.
    throw new Error('sentry-test-frontend');
  };
  return (
    <button type="button" style={style} onClick={onClick} title="Dev only">
      sentry test
    </button>
  );
}

// Per-route meta. Mirrors the resolution in App() and Shared.jsx: prefer
// hash if present, otherwise fall back to pathname (used by pre-rendered
// static pages like /despre, /termeni, etc.).
function currentRoute() {
  const hash = window.location.hash.replace(/^#/, '');
  if (hash) return hash.split('?')[0];
  return window.location.pathname || '/';
}

const initialRoute = currentRoute();
applyRouteMeta(initialRoute);
initRouteTracking(initialRoute);

const handleRouteChange = () => {
  const route = currentRoute();
  applyRouteMeta(route);
  trackRouteChange(route);
};
window.addEventListener('hashchange', handleRouteChange);
window.addEventListener('popstate', handleRouteChange);

// ErrorBoundary catches render-phase errors anywhere in the tree. The fallback
// is a deliberately plain Romanian message — we'd rather ship a known-good
// recovery than a fancy boundary that itself fails. Reload re-routes to / via
// the hash bootstrap above.
function CrashFallback() {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        fontFamily: 'system-ui, sans-serif',
        background: '#FDF9F3',
        color: '#1A1A1A',
        textAlign: 'center',
      }}
    >
      <h1 style={{ fontSize: 24, marginBottom: 8 }}>Ceva nu a mers bine</h1>
      <p style={{ marginBottom: 16, maxWidth: 420 }}>
        A apărut o eroare neașteptată. Echipa noastră a fost notificată.
      </p>
      <button
        type="button"
        onClick={() => {
          window.location.hash = '/';
          window.location.reload();
        }}
        style={{
          padding: '10px 18px',
          background: '#1A1A1A',
          color: '#FDF9F3',
          border: 'none',
          borderRadius: 8,
          cursor: 'pointer',
          fontSize: 15,
        }}
      >
        Reîncarcă pagina
      </button>
    </div>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <Sentry.ErrorBoundary fallback={<CrashFallback />}>
    <App />
  </Sentry.ErrorBoundary>,
);
